/**
 * PulseFit Workouts View Controller
 * 
 * Manages workout activity card feed, week pagination, category filtering,
 * live search, and IDOR-safe workout deletion.
 */

import {
  getStationBadgeHtml,
  isRawStationSequenceNotes
} from '../telemetry/index.js';
import {
  getWorkoutTimestamp
} from '../performance/index.js';
import {
  store,
  subscribeKey,
  saveStateToStorage,
  getMondayOfCurrentWeek
} from '../../store.js';

export { getMondayOfCurrentWeek };

export function escapeHtml(unsafe) {
  if (typeof unsafe !== 'string') return unsafe;
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function isDistanceStation(stationName, type) {
  if (type === 'Run' || type === 'RoxZone' || type === 'Roxzone' || type === 'Cardio') return true;
  if (!stationName) return false;
  const s = String(stationName).trim();
  const lower = s.toLowerCase();
  if (lower.includes('rox') || /^run\b/i.test(s)) return true;
  const baseName = s.includes(':') ? s.split(':')[0].trim() : s;
  const baseLower = baseName.toLowerCase();
  const distanceStations = [
    'rowing', 'rower', 'skierg', 'skier', 'sled push', 'sled pull',
    'farmers carry', "farmer's carry", 'farmer carry',
    'sandbag lunges', 'walking sbag lunges', 'roxzone', 'rox zone'
  ];
  return distanceStations.includes(lower) || distanceStations.includes(baseLower);
}

// Internal pagination & filter state
let currentWeekStart = getMondayOfCurrentWeek(new Date());
let activeCategoryFilter = 'all';
let activeSearchQuery = '';

// Persistent cross-week selection state (decoupled from DOM)
let selectedWorkoutIds = [];

export function getSelectedWorkoutIds() {
  return [...selectedWorkoutIds];
}

export function setSelectedWorkoutIds(ids) {
  selectedWorkoutIds = Array.isArray(ids) ? ids.map(String) : [];
  updateSummarizeButtonState();
}

export function clearWorkoutSelection() {
  selectedWorkoutIds = [];
  if (typeof document !== 'undefined') {
    const listContainer = document.getElementById('workout-list');
    if (listContainer) {
      listContainer.querySelectorAll('.workout-select-checkbox:checked').forEach(cb => {
        cb.checked = false;
      });
    }
  }
  updateSummarizeButtonState();
}

export function toggleWorkoutSelection(workoutId) {
  const strId = String(workoutId);
  const idx = selectedWorkoutIds.indexOf(strId);
  if (idx !== -1) {
    selectedWorkoutIds.splice(idx, 1);
  } else {
    if (selectedWorkoutIds.length >= 2) {
      // FIFO Auto-replace: evict the oldest selected workout
      selectedWorkoutIds.shift();
    }
    selectedWorkoutIds.push(strId);
  }

  // Synchronize visible checkboxes in DOM
  if (typeof document !== 'undefined') {
    const listContainer = document.getElementById('workout-list');
    if (listContainer) {
      listContainer.querySelectorAll('.workout-select-checkbox').forEach(cb => {
        const id = cb.getAttribute('data-id');
        cb.checked = selectedWorkoutIds.includes(String(id));
      });
    }
  }

  updateSummarizeButtonState();
}

export function updateSummarizeButtonState() {
  if (typeof document === 'undefined') return;
  const sumBtn = document.getElementById('btn-summarize-selected');
  const compBtn = document.getElementById('btn-compare-selected');
  const statusLbl = document.getElementById('ai-summary-status');
  if (!statusLbl) return;

  const allWorkouts = store.workouts || [];
  const selectedWorkouts = selectedWorkoutIds
    .map(id => allWorkouts.find(w => String(w.id) === String(id)))
    .filter(Boolean);

  if (selectedWorkouts.length === 2) {
    const w1 = selectedWorkouts[0];
    const w2 = selectedWorkouts[1];
    if (sumBtn) sumBtn.disabled = true;
    if (compBtn) compBtn.disabled = false;
    statusLbl.innerHTML = `Ready to compare <span style="color:var(--color-primary); font-weight:700;">${escapeHtml(w1.type || 'Workout')} (${w1.date})</span> and <span style="color:var(--color-primary); font-weight:700;">${escapeHtml(w2.type || 'Workout')} (${w2.date})</span>. <a href="#" id="btn-clear-selection" style="color:var(--text-muted); margin-left: 8px; text-decoration: underline; font-size: 11px;">[Clear]</a>`;
  } else if (selectedWorkouts.length === 1) {
    const w = selectedWorkouts[0];
    if (sumBtn) sumBtn.disabled = false;
    if (compBtn) compBtn.disabled = true;
    statusLbl.innerHTML = `Ready to summarize <span style="color:var(--color-primary); font-weight:700;">${escapeHtml(w.type || 'Workout')}</span> (${w.date}). Check 1 more workout (any week) to compare. <a href="#" id="btn-clear-selection" style="color:var(--text-muted); margin-left: 8px; text-decoration: underline; font-size: 11px;">[Clear]</a>`;
  } else {
    if (sumBtn) sumBtn.disabled = true;
    if (compBtn) compBtn.disabled = true;
    statusLbl.innerText = 'Check 1 box to summarize, or check 2 boxes across any week to compare workouts.';
  }

  const clearLink = document.getElementById('btn-clear-selection');
  if (clearLink) {
    clearLink.addEventListener('click', (e) => {
      e.preventDefault();
      clearWorkoutSelection();
    });
  }
}

export function getCurrentWeekStart() {
  return currentWeekStart;
}

export function setCurrentWeekStart(date) {
  currentWeekStart = date;
}

export function isDateInCurrentWeek(dateInput) {
  if (!dateInput) return false;
  let targetDate;
  if (typeof dateInput === 'string') {
    const parts = dateInput.split('T')[0].split('-');
    if (parts.length === 3) {
      targetDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0);
    } else {
      targetDate = new Date(dateInput);
    }
  } else if (typeof dateInput === 'number') {
    targetDate = new Date(dateInput);
  } else if (dateInput instanceof Date) {
    targetDate = dateInput;
  }
  if (!targetDate || isNaN(targetDate.getTime())) return false;
  
  const monday = getMondayOfCurrentWeek(currentWeekStart);
  const nextMonday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7, 0, 0, 0, 0);
  
  return targetDate >= monday && targetDate < nextMonday;
}

export function getWeeklyDateRangeString() {
  const monday = getMondayOfCurrentWeek(currentWeekStart);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6, 23, 59, 59, 999);
  
  const optionsMonth = { month: 'short' };
  const optionsDay = { day: 'numeric' };
  
  const startMonth = monday.toLocaleDateString('en-US', optionsMonth);
  const startDay = monday.toLocaleDateString('en-US', optionsDay);
  const endMonth = sunday.toLocaleDateString('en-US', optionsMonth);
  const endDay = sunday.toLocaleDateString('en-US', optionsDay);
  const year = sunday.getFullYear();
  
  if (startMonth === endMonth) {
    return `Week of ${startMonth} ${startDay} - ${endDay}, ${year}`;
  } else {
    return `Week of ${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }
}

export function navigateWeek(direction) {
  const realCurrentMonday = getMondayOfCurrentWeek(new Date());

  if (direction === 0) {
    currentWeekStart = realCurrentMonday;
  } else {
    const targetDate = new Date(
      currentWeekStart.getFullYear(),
      currentWeekStart.getMonth(),
      currentWeekStart.getDate() + (direction * 7),
      12, 0, 0, 0
    );
    const calculatedMonday = getMondayOfCurrentWeek(targetDate);
    
    if (direction > 0 && calculatedMonday.getTime() > realCurrentMonday.getTime()) {
      currentWeekStart = realCurrentMonday;
    } else {
      currentWeekStart = calculatedMonday;
    }
  }

  if (store) {
    store.currentWeekStart = currentWeekStart;
  }

  updateWeekUI();
}

export function updateWeekUI() {
  if (typeof document === 'undefined') return;

  const realCurrentMonday = getMondayOfCurrentWeek(new Date());
  const isCurrentWeek = currentWeekStart.getTime() >= realCurrentMonday.getTime();
  
  const currentDateEl = document.getElementById('current-date');
  if (currentDateEl) {
    currentDateEl.innerText = getWeeklyDateRangeString();
  }

  const nextWeekBtn = document.getElementById('btn-next-week');
  if (nextWeekBtn) {
    nextWeekBtn.disabled = isCurrentWeek;
    nextWeekBtn.style.opacity = isCurrentWeek ? '0.25' : '1';
    nextWeekBtn.style.cursor = isCurrentWeek ? 'default' : 'pointer';
    nextWeekBtn.style.pointerEvents = isCurrentWeek ? 'none' : 'auto';
  }

  const currentWeekBtn = document.getElementById('btn-current-week');
  if (currentWeekBtn) {
    currentWeekBtn.style.display = isCurrentWeek ? 'none' : 'inline-flex';
  }

  if (typeof window !== 'undefined') {
    if (typeof window.renderDashboard === 'function') window.renderDashboard();
    if (typeof window.renderAchievements === 'function') window.renderAchievements();
  }
  renderWorkouts();
}

export function filterWorkouts(category) {
  activeCategoryFilter = category || 'all';
  renderWorkouts();
}

export function setupWorkoutsSearch() {
  if (typeof document === 'undefined') return;
  const searchInput = document.getElementById('input-workout-search');
  if (!searchInput) return;

  searchInput.addEventListener('input', (e) => {
    activeSearchQuery = (e.target.value || '').trim().toLowerCase();
    renderWorkouts();
  });
}

export function renderWorkouts() {
  if (typeof document === 'undefined') return;

  const listContainer = document.getElementById('workout-list');
  const totalWorkoutsLbl = document.getElementById('stats-total-workouts');
  const totalMinutesLbl = document.getElementById('stats-total-minutes');
  const totalCaloriesLbl = document.getElementById('stats-total-calories');
  
  if (!listContainer) return;

  const allWorkouts = store.workouts || [];
  
  const visibleWorkouts = allWorkouts.filter(w => {
    const ts = getWorkoutTimestamp(w);
    if (!ts) return false;
    const localDateStr = new Date(ts).toLocaleDateString('sv');
    if (!isDateInCurrentWeek(localDateStr)) return false;

    // Category filter
    if (activeCategoryFilter !== 'all') {
      const typeLower = (w.type || '').toLowerCase();
      if (activeCategoryFilter === 'hyrox' && !typeLower.includes('hyrox')) return false;
      if (activeCategoryFilter === 'run' && !typeLower.includes('run')) return false;
      if (activeCategoryFilter === 'strength' && !typeLower.includes('strength')) return false;
    }

    // Search query filter
    if (activeSearchQuery) {
      const titleMatch = (w.name || w.type || '').toLowerCase().includes(activeSearchQuery);
      const notesMatch = (w.notes || '').toLowerCase().includes(activeSearchQuery);
      const dateMatch = (w.date || '').toLowerCase().includes(activeSearchQuery);
      if (!titleMatch && !notesMatch && !dateMatch) return false;
    }

    return true;
  });

  const totalCount = visibleWorkouts.length;
  const totalDuration = visibleWorkouts.reduce((acc, curr) => acc + (curr.duration || 0), 0);
  const totalCal = visibleWorkouts.reduce((acc, curr) => acc + (curr.calories || 0), 0);
  
  if (totalWorkoutsLbl) totalWorkoutsLbl.innerText = totalCount.toLocaleString();
  if (totalMinutesLbl) totalMinutesLbl.innerText = `${Math.round(totalDuration).toLocaleString()} min`;
  if (totalCaloriesLbl) totalCaloriesLbl.innerText = `${Math.round(totalCal).toLocaleString()} kcal`;
  
  if (visibleWorkouts.length === 0) {
    listContainer.innerHTML = `
      <div class="empty-state">
        <p>No workouts logged yet. Start sweating to fill your log!</p>
      </div>
    `;
    return;
  }
  
  listContainer.innerHTML = '';
  
  const sortedWorkouts = [...visibleWorkouts].sort((a, b) => getWorkoutTimestamp(b) - getWorkoutTimestamp(a));
  
  sortedWorkouts.forEach(workout => {
    const item = document.createElement('div');
    item.className = 'workout-item';
    
    let emoji = '💪';
    if (workout.type === 'Run') emoji = '🏃';
    else if (workout.type === 'Cycle') emoji = '🚴';
    else if (workout.type === 'Strength') emoji = '🏋️';
    else if (workout.type === 'Walk') emoji = '🚶';
    else if (workout.type === 'Yoga') emoji = '🧘';
    else if (workout.type === 'Swim') emoji = '🏊';
    else if (workout.type === 'Dance') emoji = '💃';
    else if (workout.type === 'Hyrox' || workout.type === 'Hyrox Training') emoji = '⚡';
    
    let stageToggleBtn = '';
    let stageDetailsHtml = '';
    
    if (workout.stages && Array.isArray(workout.stages) && workout.stages.length > 0) {
      stageToggleBtn = `
        <button class="btn-toggle-stages" id="toggle-btn-${workout.id}">
          <span>Show Stage Splits</span>
          <svg viewBox="0 0 24 24" width="12" height="12" style="transition: transform var(--transition-fast); margin-left: 2px;"><path fill="currentColor" d="M7 10l5 5 5-5H7z"/></svg>
        </button>
      `;
      
      stageDetailsHtml = `
        <div class="hyrox-stages-details hidden" id="stages-details-${workout.id}">
          <div class="hyrox-timeline">
            ${workout.stages.map(st => {
              const typeClass = (st.type || '').toLowerCase();
              let title = '';
              const isRun = st.type === 'Run';
              const isRox = st.type === 'RoxZone' || st.type === 'Roxzone' || st.stationName === 'RoxZone' || st.stationName === 'Roxzone' || (st.stationName && st.stationName.toLowerCase().includes('rox'));
              const stationKey = isRun ? 'Run' : (isRox ? 'RoxZone' : (st.stationName || 'Run'));
              const badge = getStationBadgeHtml(stationKey, 'station-badge-xs');
              if (isRun) {
                const rawDist = st.distance !== null && st.distance !== undefined ? st.distance : st.distance_meters;
                const distVal = Number.isFinite(Number(rawDist)) && Number(rawDist) > 0 ? Number(rawDist) : 0;
                const distText = `${distVal}m`;
                title = `${badge} <span>Run (${distText})</span>`;
              } else {
                const isDistance = isDistanceStation(st.stationName, st.type);
                const rawDist = st.distance !== null && st.distance !== undefined ? st.distance : st.distance_meters;
                const distVal = Number.isFinite(Number(rawDist)) && Number(rawDist) > 0 ? Number(rawDist) : 0;
                const repsVal = Number(st.reps) > 0 ? Number(st.reps) : (!isDistance && distVal > 0 ? distVal : 0);
                const metricValStr = isDistance 
                  ? (distVal > 0 ? `${distVal}m` : '') 
                  : (repsVal > 0 ? `${repsVal} reps` : '');
                const escapedStationName = escapeHtml(st.stationName || 'RoxZone');
                title = `${badge} <span>${escapedStationName}${metricValStr ? ` (${metricValStr})` : ''}</span>`;
              }
              const durationStr = `${st.durationMin || 0}:${(st.durationSec || 0).toString().padStart(2, '0')}`;
              return `
                <div class="hyrox-timeline-item ${typeClass}">
                  <div style="display: flex; flex-direction: column;">
                    <span class="timeline-stage-title">${title}</span>
                    <span class="timeline-stage-meta">Stage ${st.stageNum || 1}</span>
                  </div>
                  <span class="timeline-stage-duration">${durationStr}</span>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }
    
    item.style.flexDirection = 'column';
    item.style.alignItems = 'stretch';
    
    const workoutTitle = workout.name ? escapeHtml(workout.name) : `${escapeHtml(workout.type || 'Workout')} Workout`;

    let summaryParagraph = '';
    const rawNote = (workout.aiSummary && !isRawStationSequenceNotes(workout.aiSummary) && isRawStationSequenceNotes(workout.notes))
      ? workout.aiSummary
      : (workout.notes || workout.aiSummary || '');
    if (rawNote) {
      let text = rawNote.trim();
      if (text.startsWith('Synced: "') && text.endsWith('"')) {
        text = text.slice(9, -1);
      }
      
      const lines = text.split('\n');
      const collectedSentences = [];
      
      for (let line of lines) {
        let trimmed = line.trim();
        if (!trimmed) continue;
        
        trimmed = trimmed
          .replace(/^\s*(\d+[\.\)]\s*)?\*{0,2}(Executive Summary|Key Insights|Next Focus|Performance Overview|Physiological & Pacing Analysis|Key Observations & Takeaways|Actionable Training Advice|Overview|Summary)\*{0,2}[:\-]?\s*/i, '')
          .replace(/^\s*[-*✦•]\s+/, '')
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .replace(/^"+|"+$/g, '')
          .trim();
        
        if (!trimmed) continue;
        
        const sentences = trimmed.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g);
        if (sentences) {
          for (let s of sentences) {
            let cleanS = s.trim();
            if (cleanS.length > 5) {
              if (!/[.!?]$/.test(cleanS)) cleanS += '.';
              collectedSentences.push(cleanS);
            }
          }
        }
      }
      
      if (collectedSentences.length > 0) {
        const countToTake = Math.min(collectedSentences.length, 5);
        summaryParagraph = escapeHtml(collectedSentences.slice(0, countToTake).join(' '));
      }
    }

    const isChecked = selectedWorkoutIds.includes(String(workout.id));

    item.innerHTML = `
      <!-- Clickable header row -->
      <div class="workout-card-header" style="display: flex; align-items: flex-start; justify-content: space-between; width: 100%; user-select: none;">
        <div class="workout-info" style="display: flex; align-items: flex-start; gap: 14px; width: 100%;">
          <input type="checkbox" class="workout-select-checkbox" data-id="${workout.id}" title="Select for AI Coaching Summary" style="margin-top: 4px; flex-shrink: 0;" ${isChecked ? 'checked' : ''}>
          <div class="workout-icon-wrap" style="margin-top: 2px;">${emoji}</div>
          <div class="workout-details" style="flex: 1; min-width: 0;">
            <span class="workout-title">${workoutTitle}</span>
            <div class="workout-meta" style="margin-top: 2px;">
              <span>${workout.date}${workout.time ? ` at ${workout.time}` : ''}</span>
            </div>
            ${summaryParagraph ? `
              <div class="workout-summary-snippet" style="font-size: 12.5px; line-height: 1.5; color: var(--text-secondary); margin-top: 6px; font-style: italic; word-break: break-word;">
                "${summaryParagraph}"
              </div>
            ` : ''}
            ${stageToggleBtn}
          </div>
        </div>
      </div>

      <!-- Static expanded card body -->
      <div class="workout-card-body" style="padding-top: 10px; width: 100%;">
        <div style="display: flex; align-items: center; justify-content: flex-end; gap: 14px;">
          <div class="w-stat">
            <span class="w-stat-val">${workout.duration || 0}m</span>
            <span class="w-stat-lbl">Duration</span>
          </div>
          <div class="w-stat">
            <span class="w-stat-val">${workout.calories || 0} kcal</span>
            <span class="w-stat-lbl">Burned</span>
          </div>
          <button class="btn-favorite-toggle ${workout.isFavorite ? 'is-favorite' : ''}" data-id="${workout.id}" title="Favorite Workout">
            <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
          </button>
          <button class="delete-workout-btn" data-id="${workout.id}" title="Delete Workout">
            <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          </button>
        </div>
        ${workout.isFavorite ? `
          <button class="btn-repeat-workout" data-id="${workout.id}">Repeat this workout</button>
        ` : ''}
        ${stageDetailsHtml}
      </div>
    `;
    
    // Delete Button
    const delBtn = item.querySelector('.delete-workout-btn');
    if (delBtn) {
      delBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteWorkout(workout.id);
      });
    }

    // Favorite Button
    const favBtn = item.querySelector('.btn-favorite-toggle');
    if (favBtn) {
      favBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        workout.isFavorite = !workout.isFavorite;
        saveStateToStorage();
        renderWorkouts();

        const supabase = (typeof window !== 'undefined') ? window.supabaseClient : null;
        if (supabase && store.currentUser && store.currentUser.id && !store.isGuest) {
          try {
            const { error } = await supabase
              .from('workouts')
              .update({ is_favorite: workout.isFavorite })
              .eq('id', workout.id)
              .eq('user_id', store.currentUser.id);
            if (error) {
              console.warn('Failed to sync favorite status to cloud:', error);
            }
          } catch (err) {
            console.warn('Exception syncing favorite status to cloud:', err);
          }
        }
      });
    }

    // Repeat Workout Button
    if (workout.isFavorite) {
      const repeatBtn = item.querySelector('.btn-repeat-workout');
      if (repeatBtn) {
        repeatBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (typeof window !== 'undefined' && typeof window.startFavoriteSimulation === 'function') {
            window.startFavoriteSimulation(workout.id);
          }
        });
      }
    }
    
    // Checkbox Selection
    const selectCb = item.querySelector('.workout-select-checkbox');
    if (selectCb) {
      selectCb.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      selectCb.addEventListener('change', () => {
        toggleWorkoutSelection(workout.id);
      });
    }
    
    // Hyrox stage splits toggle
    if (workout.stages && Array.isArray(workout.stages) && workout.stages.length > 0) {
      const btn = item.querySelector(`#toggle-btn-${workout.id}`);
      const details = item.querySelector(`#stages-details-${workout.id}`);
      if (btn && details) {
        const icon = btn.querySelector('svg');
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const isHidden = details.classList.contains('hidden');
          const span = btn.querySelector('span');
          if (isHidden) {
            details.classList.remove('hidden');
            if (span) span.innerText = 'Hide Stage Splits';
            if (icon) icon.style.transform = 'rotate(180deg)';
          } else {
            details.classList.add('hidden');
            if (span) span.innerText = 'Show Stage Splits';
            if (icon) icon.style.transform = 'rotate(0deg)';
          }
        });
      }
    }
    
    // Card Click -> open detail
    item.addEventListener('click', (e) => {
      if (e.target.closest('.delete-workout-btn')) return;
      if (e.target.closest('.btn-toggle-stages')) return;
      if (e.target.closest('.workout-select-checkbox')) return;
      if (e.target.closest('.btn-favorite-toggle')) return;
      if (e.target.closest('.btn-repeat-workout')) return;
      if (typeof window !== 'undefined' && typeof window.openWorkoutDetail === 'function') {
        window.openWorkoutDetail(workout.id);
      }
    });
    
    listContainer.appendChild(item);
  });

  updateSummarizeButtonState();
}

/**
 * IDOR-Safe Workout Deletion
 */
export async function deleteWorkout(id) {
  const supabase = (typeof window !== 'undefined') ? window.supabaseClient : null;

  if (supabase && store.currentUser && store.currentUser.id) {
    try {
      const { error } = await supabase
        .from('workouts')
        .delete()
        .eq('id', id)
        .eq('user_id', store.currentUser.id);
        
      if (error) {
        console.error('Error deleting workout from Supabase:', error);
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Failed to delete workout from cloud', 'error');
        }
      } else {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Workout deleted from cloud', 'success');
        }
      }
    } catch (e) {
      console.error('Exception deleting workout from Supabase:', e);
    }
  }

  const strId = String(id);
  selectedWorkoutIds = selectedWorkoutIds.filter(selectedId => selectedId !== strId);
  updateSummarizeButtonState();

  store.workouts = (store.workouts || []).filter(w => w.id !== id);
  saveStateToStorage();
  renderWorkouts();
  if (typeof window !== 'undefined') {
    if (typeof window.renderDashboard === 'function') window.renderDashboard();
    if (typeof window.renderAchievements === 'function') window.renderAchievements();
  }
}

let isWorkoutsInitialized = false;

export function initWorkoutsView() {
  if (typeof document === 'undefined') return;
  if (isWorkoutsInitialized) return;
  isWorkoutsInitialized = true;

  const prevWeekBtn = document.getElementById('btn-prev-week');
  if (prevWeekBtn) {
    prevWeekBtn.addEventListener('click', () => navigateWeek(-1));
  }

  const nextWeekBtn = document.getElementById('btn-next-week');
  if (nextWeekBtn) {
    nextWeekBtn.addEventListener('click', () => navigateWeek(1));
  }

  const currentWeekBtn = document.getElementById('btn-current-week');
  if (currentWeekBtn) {
    currentWeekBtn.addEventListener('click', () => navigateWeek(0));
  }

  const currentDateEl = document.getElementById('current-date');
  if (currentDateEl) {
    currentDateEl.addEventListener('click', () => {
      const realCurrentMonday = getMondayOfCurrentWeek(new Date());
      if (currentWeekStart.getTime() < realCurrentMonday.getTime()) {
        navigateWeek(0);
      }
    });
  }

  const compBtn = document.getElementById('btn-compare-selected');
  if (compBtn) {
    compBtn.addEventListener('click', () => {
      const allWorkouts = store.workouts || [];
      const selectedWorkouts = selectedWorkoutIds
        .map(id => allWorkouts.find(w => String(w.id) === String(id)))
        .filter(Boolean);
      if (selectedWorkouts.length === 2) {
        if (typeof window !== 'undefined' && typeof window.openWorkoutComparison === 'function') {
          window.openWorkoutComparison(selectedWorkouts[0], selectedWorkouts[1]);
        } else if (typeof window !== 'undefined' && typeof window.generateAIComparison === 'function') {
          window.generateAIComparison(selectedWorkouts[0], selectedWorkouts[1]);
        }
      }
    });
  }

  const sumBtn = document.getElementById('btn-summarize-selected');
  if (sumBtn) {
    sumBtn.addEventListener('click', () => {
      const allWorkouts = store.workouts || [];
      const selectedWorkouts = selectedWorkoutIds
        .map(id => allWorkouts.find(w => String(w.id) === String(id)))
        .filter(Boolean);
      if (selectedWorkouts.length === 1) {
        if (typeof window !== 'undefined' && typeof window.generateAISummary === 'function') {
          window.generateAISummary(selectedWorkouts[0]);
        }
      }
    });
  }

  setupWorkoutsSearch();
  subscribeKey('workouts', () => renderWorkouts());
  updateSummarizeButtonState();
}
